import { useState, useMemo, useCallback, useRef } from 'react';
import type { RowSelectionState } from '@tanstack/react-table';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Checkbox } from '~/components/ui/checkbox';
import { Badge } from '~/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '~/components/ui/dialog';
import { useEligibleStudentsQuery } from './useEligibleStudentsQuery';
import { useBulkAddStudentsToClass } from './useRosterMutations';
import { useRowSelection } from '~/hooks/useRowSelection';

interface AddStudentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classOfferingId: string;
  schoolYearId: string;
  courseGradeLevels: string[];
  existingPersonIds: Set<string>;
  capacity: number | null;
  currentCount: number;
  onComplete: () => void;
}

export default function AddStudentsDialog({
  open,
  onOpenChange,
  classOfferingId,
  schoolYearId,
  courseGradeLevels,
  existingPersonIds,
  capacity,
  currentCount,
  onComplete,
}: AddStudentsDialogProps) {
  const { data, loading } = useEligibleStudentsQuery(open ? schoolYearId : undefined);
  const [bulkAdd, { loading: adding }] = useBulkAddStudentsToClass();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');
  const shiftKey = useRef(false);

  // Filter to enrolled students not already on roster, optionally matching grade level
  const eligibleStudents = useMemo(() => {
    const enrollments = (data?.schoolYear?.studentEnrollments ?? []) as any[];
    return enrollments
      .filter((se: any) => {
        if (se.state === 'WITHDRAWN' || se.state === 'DENIED' || se.state === 'NOT_RETURNING') return false;
        if (existingPersonIds.has(se.person.id)) return false;
        return true;
      })
      .map((se: any) => ({
        personId: se.person.id,
        firstName: se.person.firstName,
        lastName: se.person.lastName,
        gradeLevel: se.gradeLevel,
        matchesGrade: courseGradeLevels.length === 0 || courseGradeLevels.includes(se.gradeLevel),
      }))
      .sort((a, b) => {
        // Matching grade levels first, then alphabetical
        if (a.matchesGrade !== b.matchesGrade) return a.matchesGrade ? -1 : 1;
        return a.lastName.localeCompare(b.lastName);
      });
  }, [data, existingPersonIds, courseGradeLevels]);

  const filteredStudents = useMemo(() => {
    if (!search.trim()) return eligibleStudents;
    const q = search.toLowerCase();
    return eligibleStudents.filter(
      (s) =>
        s.firstName.toLowerCase().includes(q) ||
        s.lastName.toLowerCase().includes(q) ||
        s.gradeLevel?.toLowerCase().includes(q)
    );
  }, [eligibleStudents, search]);

  const spotsRemaining = capacity ? capacity - currentCount : null;
  const selection = useRowSelection({
    visibleRowIds: filteredStudents.map((student) => student.personId),
    rowSelection,
    setRowSelection,
  });
  const selected = useMemo(
    () => new Set(Object.keys(rowSelection).filter((personId) => rowSelection[personId])),
    [rowSelection]
  );

  const handleAdd = useCallback(async () => {
    if (selected.size === 0) return;
    await bulkAdd({
      variables: {
        input: {
          classOfferingId,
          personIds: Array.from(selected),
        },
      },
    });
    setRowSelection({});
    setSearch('');
    onOpenChange(false);
    onComplete();
  }, [selected, bulkAdd, classOfferingId, onOpenChange, onComplete]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Students</DialogTitle>
        </DialogHeader>

        <Input
          placeholder="Search by name or grade..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-2"
        />

        {loading ? (
          <p className="text-muted-foreground text-center py-4">Loading students...</p>
        ) : filteredStudents.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">No eligible students found.</p>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <Checkbox
                  checked={
                    selection.allVisibleSelected ||
                    (selection.someVisibleSelected && 'indeterminate')
                  }
                  onCheckedChange={(checked) => selection.toggleAllVisible(!!checked)}
                />
                Select All ({filteredStudents.length} eligible)
              </label>
            </div>

            <div className="flex-1 overflow-y-auto border rounded-md max-h-[40vh]">
              {filteredStudents.map((student) => (
                <label
                  key={student.personId}
                  className="flex items-center gap-3 p-2.5 border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                >
                  <Checkbox
                    checked={selected.has(student.personId)}
                    onClick={(event) => {
                      shiftKey.current = event.shiftKey;
                    }}
                    onCheckedChange={(checked) => {
                      selection.toggleRow(student.personId, !!checked, shiftKey.current);
                      shiftKey.current = false;
                    }}
                  />
                  <span className="flex-1 text-sm">
                    {student.lastName}, {student.firstName}
                  </span>
                  <Badge
                    variant={student.matchesGrade ? 'default' : 'secondary'}
                    className={student.matchesGrade ? 'bg-green-100 text-green-800' : ''}
                  >
                    {student.gradeLevel || 'No grade'}
                  </Badge>
                </label>
              ))}
            </div>
          </>
        )}

        <DialogFooter className="mt-2">
          <div className="flex-1 text-sm text-muted-foreground">
            {selected.size} selected
            {spotsRemaining !== null && ` / ${spotsRemaining} spots remaining`}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={adding || selected.size === 0}>
            {adding ? 'Adding...' : `Add ${selected.size} Student${selected.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
